// src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getProfileData, getLeaderboard, getSeasons, deletePointEntry, getGlobalHistory, getCategories, getMyXpStats, getQuests } from '../services/api';
import Leaderboard from './Leaderboard';
import HistoryList from './HistoryList';
import AdminPanel from './AdminPanel';
import Avatar from './Avatar';
import Modal from './Modal';
import DailyReward from './DailyReward';
import '../App.css';
import pointsIcon from '../assets/points_icon.png';
import banner from '../assets/banner.png';
import AchievementsModal from './AchievementsModal';
import SettingsModal from './SettingsModal';
import TransferModal from './TransferModal';
import ShopModal from './ShopModal';
import Pagination from './Pagination';
import XpWidget from './XpWidget';
import LevelTableModal from './LevelTableModal';
import BettingModal from './betting/BettingModal';
import QuestsModal from '../components/QuestsModal'
import { subscribeUserToPush, unsubscribeUserFromPush } from '../services/notifications';

export default function Dashboard() {
    const { user, logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seasons, setSeasons] = useState([]);
    const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('personal');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
    const [isAchievementsModalOpen, setIsAchievementsModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isShopModalOpen, setIsShopModalOpen] = useState(false);
    const [isLevelTableOpen, setIsLevelTableOpen] = useState(false);
    const [historyData, setHistoryData] = useState({ entries: [], pagination: {} });
    const [isBettingModalOpen, setIsBettingModalOpen] = useState(false);
    const [showRetry, setShowRetry] = useState(false);
    const [historyFilters, setHistoryFilters] = useState({
        page: 1,
        category: '',
        showRewards: true,
    });
    const [historyLoading, setHistoryLoading] = useState(true);
    const [allCategories, setAllCategories] = useState([]);
    const [xpStats, setXpStats] = useState(null);
    const [showQuests, setShowQuests] = useState(false);
    const [unclaimedQuests, setUnclaimedQuests] = useState(0);

    const fetchHistory = useCallback(async (tab, filters) => {
        setHistoryLoading(true);
        const params = {
            page: filters.page,
            showRewards: filters.showRewards,
        };
        if (filters.category) {
            params.category = filters.category;
        }

        const res = tab === 'personal'
            ? await getProfileData(params)
            : await getGlobalHistory(params);

        const data = tab === 'personal'
            ? { entries: res.data.history, pagination: res.data.pagination }
            : res.data;

        setHistoryData(data);
        setHistoryLoading(false);
    }, []);

    useEffect(() => {
        // Функция для проверки хеша
        const handleHashChange = () => {
            if (window.location.hash === '#shop') {
                setIsShopModalOpen(true);
            } else if (window.location.hash === '#bet') {
                setIsBettingModalOpen(true);
            } else {
                setIsShopModalOpen(false);
                setIsBettingModalOpen(false);
            }
        };

        // Проверяем при первой загрузке
        handleHashChange();

        // Слушаем изменения хеша (если пользователь перейдет по ссылке внутри сайта)
        window.addEventListener('hashchange', handleHashChange);

        // Убираем слушатель при размонтировании компонента
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    useEffect(() => {
        getCategories().then(res => {
            // Сразу отфильтровываем "Награду", чтобы она не попадала в выпадающий список
            setAllCategories(res.data.filter(cat => cat !== 'Награда'));
        });
    }, []);

    // useEffect для перезагрузки истории при смене вкладок или фильтров
    useEffect(() => {
        fetchHistory(activeTab, historyFilters);
    }, [activeTab, historyFilters, fetchHistory]);

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                registration.pushManager.getSubscription().then(subscription => {
                    if (subscription) {
                        setIsSubscribed(true); // Пользователь уже подписан
                    }
                    setIsSubscriptionLoading(false);
                });
            });
        } else {
            setIsSubscriptionLoading(false); // SW не поддерживается
        }
    }, []);

    const handleSubscribe = async () => {
        try {
            await subscribeUserToPush();
            alert('Вы успешно подписались на уведомления!');
            setIsSubscribed(true);
        } catch (error) {
            console.error('Ошибка подписки на уведомления:', error);
            alert(`Не удалось подписаться на уведомления. Ошибка: ${error.message}`);
        }
    };

    const handleUnsubscribe = async () => {
        try {
            await unsubscribeUserFromPush();
            alert('Вы отписались от уведомлений.');
            setIsSubscribed(false); // Обновляем состояние кнопки
        } catch (error) {
            console.error('Ошибка отписки:', error);
            alert(`Не удалось отписаться. Ошибка: ${error.message}`);
        }
    };

    // useCallback, чтобы функция не создавалась заново при каждом рендере
    const fetchData = useCallback(async () => {
        try {
            const [profileRes, leaderboardRes, seasonsRes, xpRes] = await Promise.all([
                getProfileData(),
                getLeaderboard(),
                getSeasons(),
                getMyXpStats(),
            ]);
            setProfile(profileRes.data);
            setLeaderboard(leaderboardRes.data);
            setSeasons(seasonsRes.data);
            setXpStats(xpRes.data);
            console.log('Данные обновлены!');
        } catch (error) {
            console.error("Не удалось загрузить данные:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();

        // Запускаем таймер на 10 секунд
        const timer = setTimeout(() => {
            // Если загрузка всё еще идет, показываем кнопку
            setShowRetry(true);
        }, 10000);

        // Очищаем таймер, если компонент размонтировался или данные загрузились
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleDeleteEntry = async (entryId) => {
        try {
            await deletePointEntry(entryId);
            // После успешного удаления, просто перезагружаем все данные
            fetchData();
        } catch (error) {
            console.error("Ошибка при удалении записи:", error);
            alert("Не удалось удалить запись. Попробуйте снова.");
        }
    };
    const onTransferSuccess = () => {
        setIsTransferModalOpen(false);
        fetchData(); // Обновляем все данные после успешного перевода
    };

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;

        setHistoryFilters(prev => {
            const newFilters = {
                ...prev,
                page: 1, // Всегда сбрасываем на 1 страницу
                [name]: type === 'checkbox' ? checked : value,
            };

            if (name === 'category' && value !== '') {
                newFilters.showRewards = true;
            }

            return newFilters;
        });
    };
    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        // Сбрасываем фильтры и пагинацию к состоянию по умолчанию
        setHistoryFilters({
            page: 1,
            category: '',
            showRewards: true,
        });
    };
    const activeSeason = seasons.find(s => s.status === 'ACTIVE');
    const fetchUnclaimed = useCallback(() => {
        if (activeSeason) {
            getQuests()
                .then(res => setUnclaimedQuests(res.data.unclaimedCount || 0))
                .catch(() => { });
        }
    }, [activeSeason]);
    useEffect(() => {
        fetchUnclaimed();
    }, [fetchUnclaimed]);

    if (loading || !profile || !user) {
        return (
            <div className="loading-screen">
                <p>Загрузка данных HATCHUP POINTS...</p>
                {showRetry && (
                    <div className="retry-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <p style={{ fontSize: '0.9em', color: '#ccc' }}>Долгая загрузка? Попробуйте перезайти в профиль.</p>
                        <button
                            onClick={logout}
                            className="logout-button"
                            style={{
                                padding: '10px 20px',
                                cursor: 'pointer',
                                backgroundColor: '#ff4d4d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px'
                            }}
                        >
                            Выйти из профиля
                        </button>
                    </div>
                )}
            </div>
        );
    }


    return (
        <>
            <div class="banner-container">
                <img src={banner} alt="Hatchup Points" className="login-banner" />
            </div>

            <div className="dashboard-grid">
                <div className="user-panel">
                    <div className="user-card card">
                        {profile.isAdmin && (
                            <button
                                className="admin-panel-button"
                                title="Админ-панель"
                                onClick={() => setIsAdminPanelOpen(true)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#FFFFFF">
                                    <path d="M18.75 12.75h1.5a.75.75 0 0 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM12 6a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 6ZM12 18a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 18ZM3.75 6.75h1.5a.75.75 0 1 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM5.25 18.75h-1.5a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 0 1.5ZM3 12a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 3 12ZM9 3.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5ZM12.75 12a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0ZM9 15.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
                                </svg>

                            </button>
                        )}

                        <button
                            onClick={() => setIsSettingsModalOpen(true)}
                            className="settings-icon-button"
                            title="Настройки"
                        >
                            {/* Иконка шестеренки */}
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#FFFFFF"><path d="M0 0h24v24H0V0z" fill="none" /><path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" /></svg>
                        </button>
                        <Avatar user={user} xp={xpStats?.totalXp} className="avatar" />
                        <div className="user-info">
                            <h2 className="username">{user.serverNickname || user.username}</h2>
                            <div className="points-display">{profile.totalPoints} <img src={pointsIcon} alt="общие очки" title="Общие очки" className="points-icon" />
                                <button className="transfer-button" onClick={() => setIsTransferModalOpen(true)} title="Перевести очки">
                                    Перевод
                                </button>
                            </div>

                        </div>

                    </div>
                    <XpWidget
                        stats={xpStats}
                        onLevelClick={() => setIsLevelTableOpen(true)}
                    />
                    {activeSeason && (
                        <DailyReward
                            lastClaimed={profile.lastClaimed}
                            onRewardClaimed={fetchData}
                        />
                    )}
                    {activeSeason && (
                        <div className="quests-trigger-subblock">
                            <button className="open-quests-web-btn" onClick={() => setShowQuests(true)}>
                                <span>🎯 Задания</span>
                                {unclaimedQuests > 0 && (
                                    <span className="unclaimed-count-badge">{unclaimedQuests}</span>
                                )}
                            </button>
                        </div>
                    )}
                    {showQuests && (
                        <Modal isOpen={showQuests} onClose={() => { setShowQuests(false); fetchUnclaimed(); }} size="wide">
                            <QuestsModal onClose={() => setShowQuests(false)} onRewardClaimed={() => { fetchData(); fetchUnclaimed(); }} />
                        </Modal>
                    )}
                    <Link to="/inventory" className="inventory-link-button">
                        Инвентарь
                    </Link>
                    <div className="modal-group">
                        <button
                            className="shop-link-button"
                            onClick={() => { setIsShopModalOpen(true); window.location.hash = '#shop'; }}
                        >
                            ᚳᚣᛒᛕᚣ ᚷᚣᚶᚣᚢᚣ
                        </button>
                        <button className="betting-link-button" onClick={() => { setIsBettingModalOpen(true); window.location.hash = '#bet'; }}>
                            htcpbet
                        </button>
                    </div>
                    <div className="season-info card">
                        {activeSeason ? (
                            <>
                                <h5>Текущий сезон</h5>
                                <div className="points-display seasonal">
                                    {profile.seasonalPoints} <img src={pointsIcon} alt="сезонные очки" className="points-icon seasonal-icon" title="Сезонные очки" />
                                </div>
                                <p className="season-name">{activeSeason.name}</p>
                                <p className="season-date">{new Date(activeSeason.startDate).toLocaleDateString()} - {new Date(activeSeason.endDate).toLocaleDateString()}</p>
                                <p className="season-timer">Осталось {Math.ceil((new Date(activeSeason.endDate) - new Date()) / (1000 * 60 * 60 * 24))} дня/дней.</p>
                            </>
                        ) : (
                            <>
                                <h5>Сезон неактивен</h5>
                                <p className="season-name">Очки не начисляются</p>
                            </>
                        )}
                        <Link to="/archive" className="archive-link">Архив сезонов</Link>
                        <button
                            className="help-button"
                            onClick={() => setIsAchievementsModalOpen(true)}
                        >
                            За что дают очки?
                        </button>
                    </div>


                </div>

                <div className="main-content">
                    <div className="tabs">
                        <button
                            className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                            onClick={() => handleTabChange('personal')}
                        >
                            Личная история
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'global' ? 'active' : ''}`}
                            onClick={() => handleTabChange('global')}
                        >
                            Общая история
                        </button>
                    </div>
                    <div className="history-filters">
                        <select name="category" value={historyFilters.category} onChange={handleFilterChange}>
                            <option value="">Все категории</option>
                            {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>

                        {historyFilters.category === '' && (
                            <label className="filter-rewards-label">
                                <input type="checkbox" name="showRewards" checked={historyFilters.showRewards} onChange={handleFilterChange} />
                                Показывать ежедневные награды
                            </label>
                        )}
                    </div>
                    <div className="tab-content">
                        {historyLoading ? <p>Загрузка истории...</p> : (
                            <>
                                <HistoryList
                                    data={historyData.entries}
                                    isAdmin={profile.isAdmin}
                                    onDelete={handleDeleteEntry}
                                    // --- ИЗМЕНЕНИЕ: Указываем, когда показывать получателя ---
                                    showRecipient={activeTab === 'global'}
                                />
                                <Pagination
                                    currentPage={historyFilters.page}
                                    totalPages={historyData.pagination?.totalPages}
                                    onPageChange={(page) => setHistoryFilters(prev => ({ ...prev, page: page }))}
                                />
                            </>
                        )}
                    </div>
                </div>
                {activeSeason && (
                    <div className="side-content"><Leaderboard data={leaderboard} /></div>
                )}

            </div>
            <Modal isOpen={isBettingModalOpen} onClose={() => { setIsBettingModalOpen(false); window.location.hash = ''; }} size="wide">
                <BettingModal profile={profile} onBet={fetchData} />
            </Modal>
            <Modal isOpen={isAdminPanelOpen} onClose={() => setIsAdminPanelOpen(false)}>
                <AdminPanel
                    onPointsGranted={() => {
                        fetchData(); // Обновляем данные на странице
                        setIsAdminPanelOpen(false); // Закрываем окно после успешного начисления
                    }}
                />
            </Modal>
            <Modal isOpen={isAchievementsModalOpen} onClose={() => setIsAchievementsModalOpen(false)}>
                <AchievementsModal />
            </Modal>
            <Modal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)}>
                <SettingsModal
                    user={user}
                    profile={profile}
                    isSubscribed={isSubscribed}
                    isSubscriptionLoading={isSubscriptionLoading}
                    onSubscribeToggle={{ on: handleSubscribe, off: handleUnsubscribe }}
                    onLogout={logout}
                    onUpdate={fetchData}
                />
            </Modal>
            <Modal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} size="narrow">
                <TransferModal currentUser={user} onTransferSuccess={onTransferSuccess} />
            </Modal>
            <Modal isOpen={isShopModalOpen} onClose={() => { setIsShopModalOpen(false); window.location.hash = ''; }}>
                <ShopModal profile={profile} onPurchaseSuccess={fetchData} activeSeason={activeSeason} />
            </Modal>
            <Modal isOpen={isLevelTableOpen} onClose={() => setIsLevelTableOpen(false)} size="wide">
                <LevelTableModal />
            </Modal>
        </>
    );
}